/**
 * --------------------------------------------------------------------------
 * Imports - External
 * --------------------------------------------------------------------------
 */
import { faCircleXmark } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, BoxProps, Link, Toolbar, Typography, styled } from '@mui/material';
import { FC, useMemo } from 'react';

/**
 * --------------------------------------------------------------------------
 * Imports - Internal
 * --------------------------------------------------------------------------
 */
import BctLogo from '@/assets/images/bct-logo-horizontal.png';
import Image from '@/components/layout/Image';
import Center from '@/components/common/Center';

/**
 * --------------------------------------------------------------------------
 * Error Types
 * --------------------------------------------------------------------------
 */
export enum ErrorTypeEnum {
    e401 = "401",
    e403 = "403",
    e404 = "404"
}

type HttpCode = {
    code: string;
    codeLabel: string;
    defaultMessage?: string;
}

const HttpCode401: HttpCode = {
    code: "401",
    codeLabel: "Unauthorized",
    defaultMessage: "User is Unauthorized."
}
const HttpCode403: HttpCode = {
    code: "403",
    codeLabel: "Forbidden",
    defaultMessage: "User is forbidden."
}
const HttpCode404: HttpCode = {
    code: "404",
    codeLabel: "Not Found",
    defaultMessage: "Not found."
}

export const ErrorTypes: Record<string, HttpCode> = {
    "401": HttpCode401,
    "Unauthorized": HttpCode401,

    "403": HttpCode403,
    "Forbidden": HttpCode403,

    "404": HttpCode404,
    "NotFound": HttpCode404
}


/**
 * --------------------------------------------------------------------------
 * Interfaces
 * --------------------------------------------------------------------------
*/
interface IErrorContentProps {
    errorType: string;
    errorStatus?: "warning" | "error";
    errorMessage?: string;
}

interface IErrorPageProps extends IErrorContentProps { }


/**
 * --------------------------------------------------------------------------
 * Components
 * --------------------------------------------------------------------------
 */

const StyledTitle = styled(Typography)(({ theme}) => ({
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        theme.palette.primary.main,
    textDecoration: 'none',
    textTransform: 'uppercase'
}))

const StyledTitleLink = styled(Link)(({theme}) => ({
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    textDecoration: 'none',
    padding: '5px 25px',
    whiteSpace: 'nowrap',
    //flex: '1 1 auto'
}));

const LogoutContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
}));

const StyledAppBar = styled(AppBar)(({ theme}) => ({
    zIndex: theme.zIndex.drawer + 1,
    background: theme.palette.common.white,
}));

const StyledToolbar = styled(Toolbar)(({ theme}) => ({
    background: theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,
    gap: '112px',
    padding: '0px 24px',
    minHeight: '57px !important',
    alignItems: 'stretch',
    justifyContent: 'space-between'
}));

export const ErrorContent: FC<IErrorContentProps> = ({
    errorType,
    errorStatus,
    errorMessage
}) => {

    const label = useMemo(() => {
        const httpCode: HttpCode | undefined = ErrorTypes[errorType];
        if (httpCode) return httpCode.code;

        return errorType;
    }, [ErrorTypes, errorType]);

    const message: string = useMemo(() => {
        if (!errorMessage) {
            const httpCode: HttpCode | undefined = ErrorTypes[errorType];
            if (httpCode && httpCode.defaultMessage) return httpCode.defaultMessage;
        }

        return errorMessage ?? "Something went wrong.";
    }, [errorType, errorMessage]);

    return (
        <Center width='100%' height='100%' flexDirection='column' ha='center'>
            <FontAwesomeIcon fontSize='5rem' color='red' icon={faCircleXmark} />
            <Typography textAlign="center" fontSize='5rem'>{label}</Typography>
            <Typography textAlign="center" fontSize='2.5rem'>{message}</Typography>
        </Center>
    )
}

const ErrorPage: FC<IErrorPageProps> = ({
    errorType,
    errorStatus,
    errorMessage
}) => {

    return (
        <>
            <StyledAppBar
                position="fixed"
            >
                <StyledToolbar>
                    {/* -- LEFT SIDE -- */}
                    <Box
                        sx={{
                            alignSelf: 'center',
                            lineHeight: 0
                        }}
                        component={Link}
                        href='/'
                    >
                        <Image
                            sx={{
                                height: 56
                            }}
                            alt="BCT Logo" 
                            src={ BctLogo }
                        />
                    </Box>
                    {/* -- RIGHT SIDE -- */}
                    <LogoutContainer>
                        {/* -- HEADER TITLE -- */}
                        <StyledTitleLink>
                            <StyledTitle 
                                variant="h5"
                            >
                                <strong>Redact Verification</strong>
                            </StyledTitle>
                        </StyledTitleLink>
                        {/* -- LOGOUT-- */}
                        <StyledTitleLink
                            href="/auth/logout"
                        >
                            <StyledTitle 
                                variant="h6"
                            >
                                Logout
                            </StyledTitle>
                        </StyledTitleLink>
                    </LogoutContainer>
                </StyledToolbar>
            </StyledAppBar>
            <Box 
               sx={{
                    display: 'flex',
                    width: '100%'
                }}
            >
                <Box
                    sx={{
                        flexGrow: 1,
                        minHeight: '100vh',
                    }}
                >
                    <ErrorContent errorType={errorType} errorStatus={errorStatus} errorMessage={errorMessage} />
                </Box>
            </Box>
        </>
    );
}

export default ErrorPage